import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Home, ArrowRight, RotateCcw } from "lucide-react";
import { Seo } from "@/components/Seo";

const ease = [0.16, 1, 0.3, 1];

const Digit = ({ children, delay }) => (
  <motion.span
    initial={{ opacity: 0, y: 40, filter: "blur(6px)" }}
    animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
    transition={{ duration: 0.6, delay, ease }}
    className="font-display font-semibold tracking-tighter text-zinc-900 leading-none text-[7rem] sm:text-[10rem] lg:text-[15rem]"
  >
    {children}
  </motion.span>
);

// Shared branded error scene: two digits framing a failed-print "0", used by
// the 404, 403 and 500 pages so they stay visually in sync.
export const ErrorState = ({ leftDigit, rightDigit, ns, seoTitle, testid, onRetry }) => {
  const { t } = useTranslation();

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 20 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 20 });
  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };

  return (
    <div onMouseMove={onMove} className="relative min-h-screen bg-zinc-50 overflow-hidden flex items-center pt-16 md:pt-20" data-testid={testid}>
      <Seo title={seoTitle} noindex />

      <div
        className="pointer-events-none absolute inset-0 opacity-[0.55]"
        style={{
          backgroundImage: "radial-gradient(#BDBDBD 1px, transparent 1px)",
          backgroundSize: "26px 26px",
          maskImage: "radial-gradient(ellipse 70% 60% at 50% 42%, black, transparent 72%)",
          WebkitMaskImage: "radial-gradient(ellipse 70% 60% at 50% 42%, black, transparent 72%)",
        }}
      />
      <div className="pointer-events-none absolute left-1/2 top-1/3 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-gold/10 blur-[130px]" />

      <div className="relative mx-auto max-w-[1100px] w-full px-6 md:px-12 py-16 text-center">
        <motion.span
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 font-semibold"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-gold animate-pulse" /> {t(`${ns}.eyebrow`)}
        </motion.span>

        <motion.div
          style={{ rotateX: rx, rotateY: ry, transformPerspective: 1200 }}
          className="mt-4 md:mt-6 flex items-center justify-center gap-1 sm:gap-3 lg:gap-5"
        >
          <Digit delay={0.05}>{leftDigit}</Digit>
          <div className="relative shrink-0">
            <div className="absolute inset-0 m-auto w-24 h-24 sm:w-36 sm:h-36 rounded-full bg-gold/15 blur-3xl" />
            <motion.img
              src="/lost-zero.webp"
              alt="A tangled failed 3D print in the shape of a zero"
              draggable="false"
              initial={{ opacity: 0, scale: 0.7 }}
              animate={{ opacity: 1, scale: 1, y: [0, -14, 0], rotate: [0, 6, 0, -6, 0] }}
              transition={{
                opacity: { duration: 0.7, delay: 0.15, ease },
                scale: { duration: 0.7, delay: 0.15, ease },
                y: { duration: 6, repeat: Infinity, ease: "easeInOut", delay: 0.9 },
                rotate: { duration: 16, repeat: Infinity, ease: "easeInOut", delay: 0.9 },
              }}
              className="relative w-28 sm:w-40 lg:w-56 drop-shadow-[0_22px_30px_rgba(0,0,0,0.22)]"
            />
          </div>
          <Digit delay={0.1}>{rightDigit}</Digit>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22, ease }}
          className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-zinc-900 leading-[1.05]"
          data-testid={`${testid}-title`}
        >
          {t(`${ns}.title`)}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-4 max-w-xl mx-auto text-base md:text-lg text-zinc-600 leading-relaxed"
        >
          {t(`${ns}.subtitle`)}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4, ease }}
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          {onRetry ? (
            <button
              onClick={onRetry}
              data-testid={`${testid}-retry-btn`}
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-zinc-700 transition-colors"
            >
              <RotateCcw strokeWidth={1.5} className="w-4 h-4" /> {t(`${ns}.retry`)}
            </button>
          ) : (
            <Link
              to="/"
              data-testid={`${testid}-home-btn`}
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-7 py-3.5 rounded-full text-sm font-medium hover:bg-zinc-700 transition-colors"
            >
              <Home strokeWidth={1.5} className="w-4 h-4" /> {t(`${ns}.home`)}
            </Link>
          )}
          <Link
            to={onRetry ? "/" : "/shop"}
            data-testid={`${testid}-secondary-btn`}
            className="group inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-900 transition-colors"
          >
            {onRetry ? t(`${ns}.home`) : t(`${ns}.shop`)}
            <ArrowRight strokeWidth={1.5} className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};
